<template>
    <div class="col-span-12 lg:col-span-6">
        <div class="flex items-center justify-between mb-6">
            <page-header class="!mb-0">Acuity Appointment Types</page-header>
            <vx-button
                v-if="onCorporate() && canManageAcuity"
                @click="showModal = true"
                size="lg"
            >
                Assign Appointment Types
                <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </div>

        <page-loading v-if="loading && !appointmentTypes.length"></page-loading>

        <template v-else-if="!loading && appointmentTypes.length === 0">
            <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                No Acuity appointment types assigned yet.
                <vx-button
                    v-if="onCorporate() && canManageAcuity"
                    @click="showModal = true"
                    size="lg"
                >
                    Assign Appointment Types
                    <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </vx-box>
        </template>

        <template v-else>
            <vx-table v-if="appointmentTypes.length" class="xl:grid-cols-[2fr_auto] mt-0" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div>Appointment Type Name</div>
                    <div>Duration</div>
                </vx-table-row>
                <vx-table-row v-for="appointmentType in appointmentTypes" :key="appointmentType.id">
                    <vx-table-row-title>
                        <span class="break-all lg:line-clamp-2" :title="appointmentType.name">{{ appointmentType.name }}</span>
                    </vx-table-row-title>
                    <vx-table-row-item>
                        <template v-slot:label>Duration:</template>
                        <span class="whitespace-nowrap">{{ appointmentType.duration }} min</span>
                    </vx-table-row-item>
                </vx-table-row>
            </vx-table>
        </template>

        <group-acuity-modal-form
            v-model:visible="showModal"
            :groupId="props.groupId"
            :currentAppointmentTypes="appointmentTypes"
            @saved="loadAppointmentTypes"
        ></group-acuity-modal-form>
    </div>
</template>

<script setup lang="ts">
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ref, onMounted } from 'vue';
import { VxBox, VxButton, VxTable, VxTableRow, VxTableRowTitle, VxTableRowItem } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupAcuityModalForm from './GroupAcuityModalForm.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { acuityClient } from '~/services';
import type { AcuityAppointmentType } from '~/services/acuity/acuityClient.types';
import { catchRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();
const canManageAcuity = Spark.isAdminOrOwner;

const props = defineProps({
    groupId: {
        type: String,
        required: true,
    },
});

const appointmentTypes = ref<Omit<AcuityAppointmentType, 'group_id'>[]>([]);
const loading = ref<boolean>(true);
const showModal = ref<boolean>(false);

const loadAppointmentTypes = async () => {
    loading.value = true;
    try {
        const response = await acuityClient.getGroupMapping({ teamId }, props.groupId);
        appointmentTypes.value = response.data.appointment_types;
    } catch (error) {
        console.error('Failed to load Acuity appointment types:', error);
        catchRequestError(error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    loadAppointmentTypes();
});
</script>
